<template>
  <v-container fluid class="pa-4 pa-md-6">
    <v-row>
      <v-col v-if="isProfessor" cols="12" lg="4">
        <v-card class="pa-4" rounded="lg">
          <v-card-title class="px-0">Publicar material</v-card-title>
          <v-card-subtitle class="px-0 pb-4">
            PDF, Word, Excel ou PowerPoint, com no máximo 20 MB.
          </v-card-subtitle>

          <v-form ref="form" @submit.prevent="publicar">
            <v-select
              v-model="form.vinculo"
              :items="vinculos"
              :loading="loadingVinculos"
              item-title="label"
              item-value="id"
              label="Disciplina e turma"
              variant="outlined"
              :rules="[required]"
            />
            <v-text-field
              v-model="form.titulo"
              label="Título"
              maxlength="200"
              counter
              variant="outlined"
              :rules="[required]"
            />
            <v-textarea
              v-model="form.descricao"
              label="Descrição (opcional)"
              rows="3"
              variant="outlined"
            />
            <v-file-input
              v-model="form.arquivo"
              label="Arquivo"
              prepend-icon="mdi-paperclip"
              variant="outlined"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp"
              show-size
              :rules="[requiredFile, validSize]"
            />
            <v-btn
              color="primary"
              type="submit"
              block
              size="large"
              :loading="saving"
              prepend-icon="mdi-cloud-upload"
            >
              Publicar
            </v-btn>
          </v-form>
        </v-card>
      </v-col>

      <v-col cols="12" :lg="isProfessor ? 8 : 12">
        <div class="d-flex align-center mb-4">
          <div>
            <h2 class="text-h5">Materiais de consulta</h2>
            <div class="text-medium-emphasis">
              {{ isProfessor ? "Materiais publicados por você" : "Organizados por disciplina" }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            icon="mdi-refresh"
            variant="text"
            :loading="loading"
            @click="carregarMateriais"
          />
        </div>

        <v-skeleton-loader v-if="loading" type="article, article" />

        <v-alert
          v-else-if="materiais.length === 0"
          type="info"
          variant="tonal"
          text="Nenhum material disponível."
        />

        <v-expansion-panels v-else multiple>
          <v-expansion-panel
            v-for="grupo in materiaisPorDisciplina"
            :key="grupo.disciplina"
          >
            <v-expansion-panel-title>
              <v-icon icon="mdi-book-open-page-variant" class="mr-3" />
              {{ grupo.disciplina }}
              <v-chip class="ml-3" size="small" variant="tonal">
                {{ grupo.items.length }}
              </v-chip>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-list lines="three">
                <template v-for="(material, index) in grupo.items" :key="material.id">
                  <v-divider v-if="index" />
                  <v-list-item class="py-3">
                    <template #prepend>
                      <v-avatar color="primary" variant="tonal">
                        <v-icon :icon="fileIcon(material.nome_original)" />
                      </v-avatar>
                    </template>

                    <v-list-item-title class="font-weight-medium">
                      {{ material.titulo }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ material.descricao || material.nome_original }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle>
                      Turma {{ material.turma }} · {{ formatSize(material.tamanho) }} ·
                      {{ formatDate(material.datetime) }}
                    </v-list-item-subtitle>

                    <template #append>
                      <v-btn
                        icon="mdi-download"
                        variant="text"
                        color="primary"
                        :loading="downloadingId === material.id"
                        @click="baixar(material)"
                      />
                      <v-btn
                        v-if="isProfessor"
                        icon="mdi-delete"
                        variant="text"
                        color="error"
                        :loading="deletingId === material.id"
                        @click="remover(material)"
                      />
                    </template>
                  </v-list-item>
                </template>
              </v-list>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :color="snackbarColor">
      {{ snackbarText }}
      <template #actions>
        <v-btn variant="text" @click="snackbar = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import store from "@/store";
import MaterialDataService from "@/assets/js/services/MaterialDataService";
import UserDataService from "@/assets/js/services/UserDataService";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

export default {
  name: "Materiais",

  data: () => ({
    deletingId: null,
    downloadingId: null,
    form: { vinculo: null, titulo: "", descricao: "", arquivo: [] },
    loading: true,
    loadingVinculos: false,
    materiais: [],
    saving: false,
    snackbar: false,
    snackbarColor: "success",
    snackbarText: "",
    vinculos: [],
  }),

  computed: {
    isProfessor() {
      return store.state.userType === "professor";
    },
    materiaisPorDisciplina() {
      const grupos = new Map();
      this.materiais.forEach((material) => {
        if (!grupos.has(material.disciplina)) {
          grupos.set(material.disciplina, []);
        }
        grupos.get(material.disciplina).push(material);
      });
      return Array.from(grupos, ([disciplina, items]) => ({ disciplina, items }));
    },
  },

  async mounted() {
    await Promise.all([
      this.carregarMateriais(),
      this.isProfessor ? this.carregarVinculos() : Promise.resolve(),
    ]);
  },

  methods: {
    required(value) {
      return !!value || "Campo obrigatório.";
    },
    requiredFile(value) {
      return (value && value.length > 0) || "Selecione um arquivo.";
    },
    validSize(value) {
      const file = value && value[0];
      return !file || file.size <= MAX_FILE_SIZE || "O arquivo deve ter no máximo 20 MB.";
    },
    notify(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    async carregarVinculos() {
      this.loadingVinculos = true;
      try {
        const response = await UserDataService.listProfessorDisciplinasTurmas();
        this.vinculos = (response.data || []).map((item) => ({
          id: item.id,
          label: `${item.disciplina} - Turma ${item.turma_codigo}${
            item.periodo_letivo ? ` - ${item.periodo_letivo}` : ""
          }`,
        }));
      } catch (error) {
        const detail = error.response?.data?.error || error.response?.data?.detail;
        const status = error.response?.status;
        this.notify(
          detail ||
            `Não foi possível carregar suas disciplinas${status ? ` (HTTP ${status})` : ""}.`,
          "error"
        );
      } finally {
        this.loadingVinculos = false;
      }
    },
    async carregarMateriais() {
      this.loading = true;
      try {
        const response = await MaterialDataService.list();
        this.materiais = response.data || [];
      } catch (error) {
        this.notify("Não foi possível carregar os materiais.", "error");
      } finally {
        this.loading = false;
      }
    },
    async publicar() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      const file = this.form.arquivo[0];
      const data = new FormData();
      data.append("vinculo", this.form.vinculo);
      data.append("titulo", this.form.titulo);
      data.append("descricao", this.form.descricao);
      data.append("arquivo", file);

      this.saving = true;
      try {
        await MaterialDataService.create(data);
        this.form = { vinculo: null, titulo: "", descricao: "", arquivo: [] };
        this.$refs.form.resetValidation();
        this.notify("Material publicado.");
        await this.carregarMateriais();
      } catch (error) {
        this.notify(error.response?.data?.error || "Erro ao publicar o material.", "error");
      } finally {
        this.saving = false;
      }
    },
    async baixar(material) {
      this.downloadingId = material.id;
      try {
        const response = await MaterialDataService.download(material.id);
        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = material.nome_original;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.notify("Não foi possível baixar o arquivo.", "error");
      } finally {
        this.downloadingId = null;
      }
    },
    async remover(material) {
      if (!window.confirm(`Remover "${material.titulo}"?`)) return;
      this.deletingId = material.id;
      try {
        await MaterialDataService.delete(material.id);
        this.notify("Material removido.");
        await this.carregarMateriais();
      } catch (error) {
        this.notify(error.response?.data?.error || "Erro ao remover o material.", "error");
      } finally {
        this.deletingId = null;
      }
    },
    formatSize(bytes) {
      return `${(Number(bytes) / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDate(value) {
      return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    },
    fileIcon(filename) {
      const extension = String(filename).split(".").pop().toLowerCase();
      if (extension === "pdf") return "mdi-file-pdf-box";
      if (["doc", "docx", "odt"].includes(extension)) return "mdi-file-word";
      if (["xls", "xlsx", "ods"].includes(extension)) return "mdi-file-excel";
      if (["ppt", "pptx", "odp"].includes(extension)) return "mdi-file-powerpoint";
      return "mdi-file";
    },
  },
};
</script>
